import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  Calendar,
  MapPin,
  Minus,
  Plus,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { PaymentDetailsForm } from "../components/PaymentDetailsForm";
import { useScrollIntoViewWhenReady } from "../hooks/useScrollIntoViewWhenReady";
import { useScrollToTopWhenLoading } from "../hooks/useScrollToTopWhenLoading";
import SEOHead from "../components/SEOHead";
import { recaptchaHeaders } from "../lib/recaptcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EventDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const ticketColumnRef = useRef(null);
  const checkoutFormRef = useRef(null);

  useScrollToTopWhenLoading(loading);
  useScrollIntoViewWhenReady(ticketColumnRef, !loading && !!event, { mobileOnly: true });

  useEffect(() => {
    if (!showCheckout) return;
    const t = window.setTimeout(() => {
      checkoutFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 180);
    return () => window.clearTimeout(t);
  }, [showCheckout]);

  useEffect(() => {
    fetchEvent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API}/events/${slug}`);
      setEvent(res.data);
      if (res.data.ticket_types?.length > 0) {
        setSelectedTicket(res.data.ticket_types[0]);
      }
    } catch {
      toast.error("Event not found");
      navigate("/events");
    } finally {
      setLoading(false);
    }
  };

  // Set once the booking row exists, so the catch below can tell a booking
  // failure apart from a payment failure.
  const bookingIdRef = useRef(null);

  const handleFormSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const totalAmount = selectedTicket.price * quantity;

      const rcHeaders = await recaptchaHeaders("submit_booking");
      const bookingRes = await axios.post(`${API}/bookings`, {
        guest_first_name: formData.guest_first_name,
        guest_last_name: formData.guest_last_name,
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        event_id: event.id,
        ticket_type_id: selectedTicket.id,
        ticket_quantity: quantity,
        booking_type: "event",
        total_amount: totalAmount,
        source: "direct",
      }, { headers: rcHeaders });

      const booking = bookingRes.data;
      bookingIdRef.current = booking.id;

      const paymentRes = await axios.post(`${API}/payments/initialize`, {
        booking_id: booking.id,
        amount: booking.total_amount,
        phone_number: formData.guest_phone,
      });

      if (!paymentRes.data.status) throw new Error("Payment initialization failed");

      const reference = paymentRes.data.reference;

      if (paymentRes.data.mock) {
        await axios.get(`${API}/payments/verify/${reference}`);
        navigate(`/payment-success?booking=${booking.booking_number}&reference=${reference}`);
        return;
      }

      navigate("/payment-processing", {
        state: {
          bookingNumber: booking.booking_number,
          bookingId: booking.id,
          reference,
          phone: formData.guest_phone,
          amount: booking.total_amount,
          customerName: formData.guest_name || `${formData.guest_first_name || ""} ${formData.guest_last_name || ""}`.trim(),
          email: formData.guest_email,
        },
      });
    } catch (error) {
      // The booking is committed before payment is attempted, so a failure here
      // is almost always the payment step. Saying "booking failed" sends people
      // back to re-submit the form, which creates a duplicate.
      const stage = bookingIdRef.current ? "payment" : "booking";
      toast.error(
        error.response?.data?.detail ||
          (stage === "payment"
            ? "Couldn't start the M-Pesa payment. Please try again — your booking is saved."
            : "Failed to create booking. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container-empire flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
      </div>
    );
  }

  if (!event) return null;

  const startDate = event.start_datetime ? parseISO(event.start_datetime) : new Date();
  const endDate = event.end_datetime ? parseISO(event.end_datetime) : new Date();

  const ticketTypeStyles = {
    early_bird: "bg-green-500/20 text-green-400 border-green-500/30",
    regular: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    vip: "bg-empire-gold/20 text-empire-gold border-empire-gold/30",
  };

  const seoDescription =
    (event.description || "").trim().slice(0, 155) ||
    `${event.name} — a dance event at Empire Dance Kenya, Westlands Nairobi. Book tickets online.`;

  const ticketOffers = (event.ticket_types || []).map((t) => ({
    "@type": "Offer",
    name: t.name,
    price: t.price,
    priceCurrency: "KES",
    availability: "https://schema.org/InStock",
    url: `https://bookings.empiredancekenya.com/events/${event.slug || slug}`,
  }));

  const eventJsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.name,
    description: event.description || undefined,
    startDate: event.start_datetime,
    endDate: event.end_datetime,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: event.cover_image || undefined,
    location: {
      "@type": "Place",
      name: event.venue_name || "Empire Dance Kenya",
      address: event.venue_address || "Westlands, Nairobi, Kenya",
    },
    organizer: {
      "@type": "Organization",
      name: "Empire Dance Kenya",
      url: "https://bookings.empiredancekenya.com",
    },
    ...(ticketOffers.length ? { offers: ticketOffers } : {}),
  };

  return (
    <div className="container-empire" data-testid="event-detail-page">
      <SEOHead
        page="events"
        title={`${event.name} | Empire Dance Kenya`}
        description={seoDescription}
        image={event.cover_image}
        path={`/events/${event.slug || slug}`}
        jsonLd={eventJsonLd}
      />
      <Button
        variant="ghost"
        onClick={() => navigate("/events")}
        className="mb-6 text-neutral-400 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Events
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Event Details */}
        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Hero Image */}
            <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden mb-8">
              <img
                src={event.cover_image || "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=800"}
                alt={event.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <Badge className={ticketTypeStyles[event.event_type] || "bg-neutral-500/20"}>
                  {event.event_type}
                </Badge>
                <h1 className="text-3xl md:text-4xl font-bold text-white mt-3">
                  {event.name}
                </h1>
              </div>
            </div>

            {/* Event Info */}
            <div className="space-y-6">
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3 text-white">
                  <div className="w-12 h-12 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-empire-gold" />
                  </div>
                  <div>
                    <p className="font-semibold">{format(startDate, "EEEE, MMMM d, yyyy")}</p>
                    <p className="text-sm text-neutral-400">
                      {format(startDate, "h:mm a")} - {format(endDate, "h:mm a")}
                    </p>
                  </div>
                </div>

                {event.venue_name && (
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-12 h-12 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-empire-gold" />
                    </div>
                    <div>
                      <p className="font-semibold">{event.venue_name}</p>
                      <p className="text-sm text-neutral-400">{event.venue_address}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="prose prose-invert max-w-none">
                <h3 className="text-xl font-semibold text-white">About this Event</h3>
                <p className="text-neutral-400">{event.description}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Ticket Selection */}
        <div ref={ticketColumnRef} className="lg:col-span-1 scroll-mt-24 md:scroll-mt-0">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="card-empire-featured sticky top-24">
              <CardHeader>
                <CardTitle className="text-white">Get Tickets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!showCheckout ? (
                  <>
                    {/* Ticket Types */}
                    {event.ticket_types?.map((ticket) => {
                      const available = ticket.quantity_available - ticket.quantity_sold;
                      const isSelected = selectedTicket?.id === ticket.id;

                      return (
                        <button
                          key={ticket.id}
                          onClick={() => setSelectedTicket(ticket)}
                          disabled={available <= 0}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all ${isSelected
                              ? "border-empire-gold bg-empire-gold/10"
                              : "border-neutral-800 hover:border-neutral-700"
                            } ${available <= 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Badge className={ticketTypeStyles[ticket.ticket_type]}>
                              {ticket.name}
                            </Badge>
                            {available <= 10 && available > 0 && (
                              <span className="text-xs text-orange-400">{available} left</span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xl font-bold text-white">
                              KES {ticket.price.toLocaleString()}
                            </span>
                            {isSelected && (
                              <div className="w-5 h-5 rounded-full bg-empire-gold flex items-center justify-center">
                                <span className="text-black text-xs">✓</span>
                              </div>
                            )}
                          </div>
                          {ticket.description && (
                            <p className="text-sm text-neutral-500 mt-2">{ticket.description}</p>
                          )}
                        </button>
                      );
                    })}

                    {/* Quantity */}
                    {selectedTicket && (
                      <div className="pt-4 border-t border-neutral-800">
                        <Label className="text-white mb-2 block">Quantity</Label>
                        <div className="flex items-center gap-4">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            disabled={quantity <= 1}
                            className="border-neutral-700"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="text-2xl font-bold text-white w-12 text-center">
                            {quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setQuantity(Math.min(selectedTicket.max_per_order, quantity + 1))}
                            disabled={quantity >= selectedTicket.max_per_order}
                            className="border-neutral-700"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Total */}
                    {selectedTicket && (
                      <div className="pt-4 border-t border-neutral-800">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-neutral-400">Total</span>
                          <span className="text-2xl font-bold text-empire-gold">
                            KES {(selectedTicket.price * quantity).toLocaleString()}
                          </span>
                        </div>
                        <Button
                          className="w-full btn-empire"
                          onClick={() => setShowCheckout(true)}
                        >
                          Continue to Checkout
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  /* Checkout Form */
                  <div ref={checkoutFormRef} className="space-y-4 scroll-mt-24">
                    <div className="flex items-center justify-between pb-3 border-b border-neutral-800 text-sm">
                      <span className="text-neutral-400">{selectedTicket?.name} × {quantity}</span>
                      <span className="text-empire-gold font-bold">
                        KES {(selectedTicket?.price * quantity).toLocaleString()}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => setShowCheckout(false)}
                      className="text-neutral-400 hover:text-white p-0 h-auto text-xs"
                    >
                      ← Back to ticket selection
                    </Button>
                    <PaymentDetailsForm
                      onSubmit={handleFormSubmit}
                      submitting={submitting}
                      amount={selectedTicket?.price * quantity}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
